import { useState, useEffect, useRef } from 'react'
import { PromptEditorModal } from './components/PromptEditorModal'
import ConfirmDialog from './components/ConfirmDialog'
import SuccessDialog from './components/SuccessDialog'
import AuthLogin from './components/AuthLogin'
import { TrocaSenhaObrigatoria } from './components/TrocaSenhaObrigatoria'
import { MetaIcon } from './components/DevicesPanel'
import { WhatsAppInboxPanel, WhatsAppInboxPanelRef } from './components/WhatsAppInboxPanel'
// MessageBlastPanel removido - funcionalidade migrada para MarketingPanel
import { ConversationsPanel } from './components/ConversationsPanel'
// SchedulePanel e SendReportPanel removidos - scheduler roda no backend independente
import { SettingsPanel, UserPermissionsPanel, CreateUserPanel } from './components/SettingsPanel'
import { NewDevicePanel } from './components/NewDevicePanel'
// NewTaskPanel removido - painel de agendamento removido da UI
import MapaDistribuicao from './components/MapaDistribuicao'
import { InfrastructureDBPanel } from './components/InfrastructureDBPanel'
import { MarketingPanel } from './components/MarketingPanel'
import { AgenteCampanhaPanel } from './components/AgenteCampanhaPanel'
import { AplicativoPanel } from './components/AplicativoPanel'
import { SistemaPanel } from './components/SistemaPanel'
import { BloqueadosPanel } from './components/BloqueadosPanel'
import type { SistemaTab } from './components/SistemaPanel'
import { ArquivosLab } from './components/ArquivosLab'
import { LeaderFollowupPanel } from './components/LeaderFollowupPanel'
import { LeaderCoverageView } from './components/LeaderCoverageView'
import { WizardCaptacaoPanel } from './components/WizardCaptacaoPanel'
import { ParetoRankingPanel } from './components/ParetoRankingPanel'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { ClaraVoiceProvider } from './contexts/ClaraVoiceContext'
import { ChatMessage, RAGDocument, TranscriptionResult, SavedTranscription, Agent } from './types'
import { generateId } from './lib/utils'
import { API_ENDPOINTS } from './lib/api'
import { FiSettings, FiSmartphone, FiUsers, FiX, FiSearch, FiCheckCircle } from 'react-icons/fi'
import { CrmSidebar, crmViewLabel, canViewPanel, firstViewablePanel, painelLiberado, primeiroPainelLiberado, type AppView, type SettingsAction, FiMenu } from './components/CrmSidebar'
import { LuUserPlus } from 'react-icons/lu'
import { useBackendHealth } from './hooks/useBackendHealth'
import AssignLeaderModal from './components/AssignLeaderModal'
import { AutoAssign247Toggle } from './components/AutoAssign247Toggle'
import './styles/flip-animation.css'

// ─────────────────────────────────────────────────────────────────────────────
// Device Meta do painel Conversas WhatsApp — PRAÇA-ESPECÍFICO.
//
// Estava fixo no id do Atendimento·TWMS: o painel do Planalto abriria as
// conversas do número de Campo Grande. Vem de env de BUILD (mesmo padrão do
// VITE_PAINEIS_LIBERADOS): a praça declara o número dela no .env e rebuilda,
// sem TSX diferente por praça. Ausente/vazio = matriz, byte a byte como antes.
// ─────────────────────────────────────────────────────────────────────────────
const WA_DEVICE_ID = (import.meta.env?.VITE_WA_DEVICE_ID || '').trim() || 'meta_762195393654543'
const WA_DEVICE_NAME = (import.meta.env?.VITE_WA_DEVICE_NAME || '').trim() || 'Atendimento - TWMS'

function MainApp() {
  const { authenticatedFetch, user, logout, hasFolderAccess, hasToolAccess } = useAuth()
  // NEU-LAB (teste): ?neu=1 estende o neumorfismo à barra de dispositivos do
  // WhatsApp (chrome do App, fora do .neu-lab do painel). Captura 1x no mount.
  const [neuLab] = useState(() => typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('neu') !== '0')
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [ragDocuments, setRagDocuments] = useState<RAGDocument[]>([])
  const [savedTranscriptions, setSavedTranscriptions] = useState<SavedTranscription[]>([])
  const [promptText, setPromptText] = useState('')
  // showReportPanel removido - painel de agendamento removido da UI
  // showBlastReportPanel removido - migrado para MarketingPanel
  const [showUserPermissions, setShowUserPermissions] = useState<{ user: any; permissions: any; devices: any[] } | null>(null) // Estado para flip de permissões
  const [settingsModal, setSettingsModal] = useState<SettingsAction | null>(null) // Modal flutuante
  const [settingsPanelKey, setSettingsPanelKey] = useState(0) // Chave para forçar refresh do SettingsPanel
  const [isPollingEnabled, setIsPollingEnabled] = useState(false) // Estado para controlar polling de mensagens
  // showNewTaskPanel removido - painel de agendamento removido da UI
  // schedulePanelKey removido - painel de agendamento removido da UI

  // Estado para controlar modo de visualização (Painel de Operações vs Mapa de Distribuição vs CRM)
  // Inicializa lendo ?panel= da URL pra suportar deep link (ex.: link "abrir nota"
  // do banner de query promovida na aba Infraestrutura DB).
  const [viewMode, setViewMode] = useState<AppView>(() => {
    // Padrão da praça: 'whatsapp' fixo só vale onde tudo está liberado. Numa
    // praça em liberação incremental ele monta um painel bloqueado (com as
    // requisições dele) só pro guard abaixo rebater — o usuário via o flash.
    const fallback = primeiroPainelLiberado()
    if (typeof window === 'undefined') return fallback
    const panel = new URLSearchParams(window.location.search).get('panel')
    // Compat legacy: 'tecnologia'/'biblioteca' viraram abas do painel unificado 'sistema'.
    if (panel === 'tecnologia' || panel === 'biblioteca') return painelLiberado('sistema') ? 'sistema' : fallback
    const allowed = ['whatsapp', 'map', 'infrastructure', 'arquivos', 'marketing', 'marketing-agente', 'aplicativo', 'sistema',
      'bloqueados',
      'lideres-acompanhamento', 'lideres-cobertura', 'lideres-captacao', 'lideres-pareto'] as const
    const ok = (allowed as readonly string[]).includes(panel || '') && painelLiberado(panel as AppView)
    return ok ? (panel as typeof allowed[number]) : fallback
  })
  // Aba inicial do painel Sistema (de ?tab=, ou derivada do panel legacy).
  const [sistemaInitialTab, setSistemaInitialTab] = useState<SistemaTab | null>(() => {
    if (typeof window === 'undefined') return null
    const p = new URLSearchParams(window.location.search)
    const panel = p.get('panel'), tab = p.get('tab')
    if (panel === 'biblioteca') return 'obsidian'  // aba Biblioteca removida → Obsidian assumiu o vault
    // Abas atuais: Infra · Deploy · Backup · Storage · Áudio · Indicações ·
    // Obsidian. Os ids antigos continuam aceitos e são remapeados pelo
    // SistemaPanel — link salvo por aí não pode cair no lugar errado.
    const allowedTabs = ['infra', 'deploy', 'backup', 'storage', 'audio', 'indicacoes', 'obsidian',
                         'monitor', 'clara', 'voz', 'transcricao', 'moss', 'admin']
    if (allowedTabs.includes(tab || '')) return tab as SistemaTab
    // `?panel=tecnologia` (nome legado do painel) sem ?tab= abre na Infra. Esta
    // checagem vem DEPOIS do tab: antes ela retornava cedo e engolia o ?tab=
    // de todo deep-link no formato legado.
    if (panel === 'tecnologia') return 'infra'
    return null
  })
  // Path inicial pra Biblioteca. Vem do deep link (?path=...) no boot e também
  // do evento 'openVaultNote' (clique num nó da Árvore da Arquitetura no painel
  // Tecnologia). É estado pra poder mudar em runtime; ao trocar pra 'biblioteca'
  // o BibliotecaPanel remonta e carrega o path no mount.
  const [bibliotecaInitialPath, setBibliotecaInitialPath] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null
    return new URLSearchParams(window.location.search).get('path')
  })
  // Deep-link NÃO é atalho pra permissão. O `?panel=` entra direto no viewMode,
  // sem passar pela sidebar — que é onde o filtro por permissão morava. Sem
  // isto, a proteção do painel ficava dependendo de cada rota do backend
  // lembrar do middleware; basta um painel novo com rota esquecida pra virar
  // exposição por URL. Cai no painel padrão em vez de mostrar erro: o usuário
  // sem acesso não precisa saber que a tela existe.
  // Roda sem corrida: o AuthWrapper só monta o MainApp depois de resolver a
  // autenticação, então `user` e permissões já estão aqui no primeiro render.
  useEffect(() => {
    if (!user) return
    const perms = { isAdmin: !!user.isAdmin, hasToolAccess, hasFolderAccess }
    if (!canViewPanel(viewMode, perms)) {
      const destino = firstViewablePanel(perms)
      console.warn(`[APP] painel "${viewMode}" bloqueado por permissão — indo pro "${destino}"`)
      setViewMode(destino)
    }
  }, [user, viewMode, hasToolAccess, hasFolderAccess])

  // Reflete o estado de navegação na URL (deep-link / refresh). App é a fonte da URL;
  // o painel Sistema comunica a aba ativa via onTabChange (→ sistemaInitialTab).
  useEffect(() => {
    if (typeof window === 'undefined') return
    const u = new URLSearchParams(window.location.search)
    u.set('panel', viewMode)
    if (viewMode === 'sistema') {
      if (sistemaInitialTab) u.set('tab', sistemaInitialTab); else u.delete('tab')
      if (sistemaInitialTab === 'biblioteca' && bibliotecaInitialPath) u.set('path', bibliotecaInitialPath); else u.delete('path')
    } else { u.delete('tab'); u.delete('path') }
    window.history.replaceState(null, '', `${window.location.pathname}?${u}`)
  }, [viewMode, sistemaInitialTab, bibliotecaInitialPath])
  // O inbox do Faturamento MIGROU pro fin.twms.com.br em 2026-07-07 (pedido do
  // diretor) — aqui ficou só o Atendimento; o toggle de device foi removido.
  const [waSearch, setWaSearch] = useState('')
  // DOM node onde a toolbar do inbox (Lista×Kanban + campanha) é renderizada via
  // portal pelo WhatsAppInboxPanel ativo (fica na barra de dispositivos).
  const [waToolbarSlot, setWaToolbarSlot] = useState<HTMLDivElement | null>(null)
  const [waDeviceInfo, setWaDeviceInfo] = useState<Record<string, { displayPhone?: string; aiEnabled?: boolean }>>({})
  const [collapsed, setCollapsed] = useState(false)   // sidebar recolhida (desktop)
  const [mobileOpen, setMobileOpen] = useState(false) // drawer da sidebar (mobile)
  // Deep-link da Cobrança → painel WhatsApp Faturamento, auto-selecionando
  // a conversa do cliente clicado. Limpado depois que o painel seleciona.
  const [pendingConversationId, setPendingConversationId] = useState<string | null>(null)

  useEffect(() => {
    if (viewMode !== 'whatsapp') return
    authenticatedFetch('/api/whatsapp-devices').then(r => r.json()).then((devices: any[]) => {
      const info: Record<string, { displayPhone?: string; aiEnabled?: boolean }> = {}
      devices.forEach(d => { if (d.id) info[d.id] = { displayPhone: d.display_phone, aiEnabled: Number(d.ai_agent_enabled) === 1 } })
      setWaDeviceInfo(info)
    }).catch(() => {})
  }, [viewMode])

  // Estado para markers do mapa de distribuição
  const [mapMarkers, setMapMarkers] = useState<Array<{
    id: string
    municipioId: string
    name: string
    coordinates: [number, number]
    data?: {
      recrutas?: number
      vendas?: number
      status?: 'active' | 'inactive'
    }
  }>>([])

  // Refs para WhatsAppInboxPanel - expõe openPromptModal para o device bar
  const waAtendimentoRef = useRef<WhatsAppInboxPanelRef>(null)

  // Health check do backend - auto-reconexão sem F5
  const backendHealth = useBackendHealth(15000)
  const [showReconnectedBanner, setShowReconnectedBanner] = useState(false)
  const prevHealthStatus = useRef(backendHealth.status)

  // Mostrar banner "Reconectado!" por 3s quando volta ao online
  useEffect(() => {
    if (prevHealthStatus.current !== 'online' && backendHealth.status === 'online') {
      setShowReconnectedBanner(true)
      setTimeout(() => setShowReconnectedBanner(false), 3000)
    }
    prevHealthStatus.current = backendHealth.status
  }, [backendHealth.status])

  // Estado para o modal de atribuir líder (CRM)
  const [showCRMLeaderModal, setShowCRMLeaderModal] = useState(false)
  const [crmLeaderModalData, setCRMLeaderModalData] = useState<any>(null)

  // Estados para ferramentas habilitadas no prompt
  const [enabledTools, setEnabledTools] = useState<string[]>([])

  // Estados para RAG inteligente
  const [selectedRagId, setSelectedRagId] = useState<string | null>(null)
  // Removido índice detalhado de RAG (simplificado no editor de prompt)
  
  // Estados para otimização de prompt
  const [isOptimizing, setIsOptimizing] = useState(false)
  
  // Estados para configuração LLM
  const [selectedLLMModel, setSelectedLLMModel] = useState('openclaw')
  
  // Estados para modal de confirmação
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean
    title: string
    message: string
    details: string[]
    onConfirm: () => void
    type: 'danger' | 'warning' | 'info'
  }>({
    isOpen: false,
    title: '',
    message: '',
    details: [],
    onConfirm: () => {},
    type: 'danger'
  })

  // Estados para modal de sucesso
  const [successDialog, setSuccessDialog] = useState<{
    isOpen: boolean
    title: string
    message: string
    details: string[]
  }>({
    isOpen: false,
    title: '',
    message: '',
    details: []
  })
  
  // Estados para persistência
  const [sessionId, setSessionId] = useState<string>(() => {
    // Carregar sessionId do localStorage na inicialização
    const savedSessionId = localStorage.getItem('currentSessionId')
    if (savedSessionId) {
      console.log('[CHAT] 📂 SessionId recuperado do localStorage:', savedSessionId)
      return savedSessionId
    }
    // Criar novo sessionId se não existir
    const newSessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
    localStorage.setItem('currentSessionId', newSessionId)
    console.log('[CHAT] 🆕 Novo sessionId criado:', newSessionId)
    return newSessionId
  })
  
  // Estados para sincronização de prompt
  // Estados de sincronização de prompt removidos (não usados na UI atual)
  
  // Estados para modais flutuantes
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false)
  const [isConversationsModalOpen, setIsConversationsModalOpen] = useState(false)

  // Estados para Multi-Agentes
  const [agents, setAgents] = useState<Agent[]>([])
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(() => {
    const savedAgentId = localStorage.getItem('selectedAgentId')
    if (savedAgentId) {
      console.log('[AGENT] 📂 AgentId recuperado do localStorage:', savedAgentId)
    }
    return savedAgentId || null
  })
  // Estado para info do agendamento
  // scheduleInfo removido - painel de agendamento removido da UI

  // Listener para o botão de atribuir líder no CRM
  useEffect(() => {
    const handleOpenLeaderModal = (event: any) => {
      console.log('Evento openAssignLeaderModal recebido:', event.detail)
      setCRMLeaderModalData(event.detail)
      setShowCRMLeaderModal(true)
    }

    const handlePostMessage = (event: MessageEvent) => {
      if (event.data.type === 'OPEN_ASSIGN_LEADER_MODAL') {
        console.log('PostMessage OPEN_ASSIGN_LEADER_MODAL recebido:', event.data)
        setCRMLeaderModalData({
          conversationId: event.data.conversationId,
          contactInfo: event.data.contactInfo
        })
        setShowCRMLeaderModal(true)
      }
    }

    // Diagrama da Arquitetura → troca pra aba Obsidian (vault). O Obsidian embutido
    // (KasmVNC) não deep-linka numa nota específica; abre o vault e o operador navega.
    const handleOpenVaultNote = (e: Event) => {
      const path = (e as CustomEvent).detail?.path
      if (typeof path !== 'string' || !path) return
      setBibliotecaInitialPath(path)   // mantido (dormente) p/ eventual retorno da Biblioteca
      setSistemaInitialTab('obsidian')
      setViewMode('sistema')
    }

    window.addEventListener('openAssignLeaderModal', handleOpenLeaderModal)
    window.addEventListener('message', handlePostMessage)
    window.addEventListener('openVaultNote', handleOpenVaultNote)

    return () => {
      window.removeEventListener('openAssignLeaderModal', handleOpenLeaderModal)
      window.removeEventListener('message', handlePostMessage)
      window.removeEventListener('openVaultNote', handleOpenVaultNote)
    }
  }, [])

  // Carregar histórico quando agente ou sessão mudar
  useEffect(() => {
    if (selectedAgentId && sessionId) {
      console.log('[CHAT] 🔄 useEffect disparado - carregando histórico...', { selectedAgentId, sessionId })
      loadChatHistory(selectedAgentId, sessionId)
    } else {
      console.log('[CHAT] ⏸️ useEffect aguardando dados...', { 
        hasAgentId: !!selectedAgentId, 
        hasSessionId: !!sessionId 
      })
    }
  }, [selectedAgentId, sessionId])

  // Função para carregar documentos RAG
  const loadRAGDocuments = async () => {
    try {
      const ragResponse = await authenticatedFetch(API_ENDPOINTS.RAG_DOCUMENTS)
      if (ragResponse.ok) {
        const ragData = await ragResponse.json()
        if (ragData.success) {
          const ragDocuments = ragData.data.map((r: any) => ({
            id: r.id,
            title: r.title,
            content: r.content,
            chunks: r.chunks,
            createdAt: r.created_at,
            sourceFileName: r.source_filename,
            metadata: r.metadata
          }))
          setRagDocuments(ragDocuments)
          console.log(`[RAG] ${ragDocuments.length} documentos RAG carregados`)
        }
      }
    } catch (error) {
      console.error('❌ Erro ao carregar documentos RAG:', error)
    }
  }

  // Função para carregar dados persistidos
  const loadPersistedData = async () => {
    try {
      console.log('🔄 Carregando dados persistidos...')

      // Carregar transcrições salvas (APENAS áudio/vídeo)
      const transcriptionsResponse = await authenticatedFetch(API_ENDPOINTS.TRANSCRIPTIONS)
      if (transcriptionsResponse.ok) {
        const transcriptionsData = await transcriptionsResponse.json()
        if (transcriptionsData.success) {
          // Converter para formato SavedTranscription com mapeamento correto
          // FILTRAR: Apenas transcrições que têm duration (áudio/vídeo) OU isAudioVideo=true
          const savedTranscriptions = transcriptionsData.data
            .filter((t: any) => {
              // Aceitar se tem duration (transcrições antigas de áudio/vídeo)
              // OU se está marcado explicitamente como isAudioVideo
              const hasAudioVideoMetadata = t.is_audio_video === 1 || t.is_audio_video === true
              const hasDuration = t.duration && t.duration > 0
              return hasAudioVideoMetadata || hasDuration
            })
            .map((t: any) => ({
              id: t.id,
              title: t.title,
              text: t.content, // content do banco → text do frontend
              fileName: t.source_filename,
              duration: t.duration,
              audioSize: t.file_size,
              characterCount: t.content ? t.content.length : 0,
              createdAt: t.created_at,
              chunks: t.metadata?.chunks || undefined
            }))
          setSavedTranscriptions(savedTranscriptions)
          console.log(`[AUDIO] ${savedTranscriptions.length} transcrições de áudio/vídeo carregadas`)
        }
      }

      // Carregar documentos RAG
      await loadRAGDocuments()

      // Recuperar ou gerar ID de sessão
      let currentSessionId = localStorage.getItem('currentSessionId')
      if (!currentSessionId) {
        currentSessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
        localStorage.setItem('currentSessionId', currentSessionId)
      }
      setSessionId(currentSessionId)
      console.log('[CHAT] Sessão de chat:', currentSessionId)

    } catch (error) {
      console.error('❌ Erro ao carregar dados persistidos:', error)
    }
  }

  // Inicialização da aplicação
  useEffect(() => {
    loadPersistedData()
    loadAgents() // Carregar agentes
    // Estados de sincronização removidos
    
    // Carregar selectedRagId do localStorage
    const savedRagId = localStorage.getItem('selectedRagId')
    if (savedRagId) {
      setSelectedRagId(savedRagId)
    }
  }, []) // Apenas na inicialização

  // Função para carregar agentes
  const loadAgents = async () => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.AGENTS)
      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          // Mapear campos do banco para o frontend
          const mappedAgents = data.data.map((agent: any) => ({
            id: agent.id,
            name: agent.name,
            emoji: agent.avatar_emoji || '🤖',
            description: agent.description || '',
            systemPrompt: agent.system_prompt || '',
            temperature: agent.temperature || 0.7,
            model: agent.model || 'gemini-2.5-flash',
            color: agent.color || '#3b82f6',
            isDefault: Boolean(agent.is_default),
            evolutionInstanceId: agent.evolution_instance_id,
            toolsPrompt: agent.toolsPrompt,  // ✅ Adicionar campo customizado
            ragPrompt: agent.ragPrompt,      // ✅ Adicionar campo customizado
            createdAt: agent.created_at,
            updatedAt: agent.updated_at
          }))
          
          setAgents(mappedAgents)
          console.log(`[AGENTS] ${mappedAgents.length} agentes carregados`)
          
          // Se não tiver agente selecionado, selecionar o padrão
          if (!localStorage.getItem('selectedAgentId') && mappedAgents.length > 0) {
            const defaultAgent = mappedAgents.find((a: Agent) => a.isDefault) || mappedAgents[0]
            setSelectedAgentId(defaultAgent.id)
            localStorage.setItem('selectedAgentId', defaultAgent.id)
          }
        }
      }
    } catch (error) {
      console.error('❌ Erro ao carregar agentes:', error)
    }
  }

  // Função para carregar histórico de mensagens do agente
  const loadChatHistory = async (agentId: string, currentSessionId: string) => {
    try {
      console.log('[CHAT] Carregando histórico de mensagens...', { agentId, currentSessionId })
      const response = await authenticatedFetch(
        `${API_ENDPOINTS.AGENTS}/${agentId}/messages?sessionId=${currentSessionId}&limit=100`
      )
      if (response.ok) {
        const data = await response.json()
        console.log('[CHAT] 📦 Resposta do backend:', data)
        
        // Backend retorna em data.data (não data.messages)
        if (data.success && data.data && data.data.length > 0) {
          // Mapear mensagens do backend para o formato do frontend
          const historyMessages: ChatMessage[] = data.data.map((msg: any) => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            timestamp: msg.created_at || new Date().toISOString(),
            metadata: msg.metadata || {}
          }))
          
          setMessages(historyMessages)
          console.log(`[CHAT] ✅ ${historyMessages.length} mensagens carregadas do histórico`)
        } else {
          console.log('[CHAT] Nenhuma mensagem antiga encontrada, começando conversa nova')
          setMessages([]) // Limpar mensagens se não houver histórico
        }
      } else {
        console.error('[CHAT] ❌ Erro na resposta:', response.status, response.statusText)
      }
    } catch (error) {
      console.error('❌ Erro ao carregar histórico de chat:', error)
      setMessages([]) // Em caso de erro, começar com conversa limpa
    }
  }

  // Função para criar novo agente
  const handleCreateAgent = async (name: string, emoji: string) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.AGENTS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          avatarEmoji: emoji,
          description: '',
          system_prompt: '',
          temperature: 0.7,
          model: selectedLLMModel
        })
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          await loadAgents()
          setSelectedAgentId(data.data.id)
          localStorage.setItem('selectedAgentId', data.data.id)
          // Limpar prompt e RAG para construir do zero
          setPromptText('')
          setSelectedRagId(null)
          console.log('✅ Agente criado:', name)
        }
      }
    } catch (error) {
      console.error('❌ Erro ao criar agente:', error)
    }
  }

  // Função para atualizar agente completo
  const handleUpdateAgentFull = async (agentId: string, data: Partial<Agent>) => {
    try {
      const agent = agents.find(a => a.id === agentId)
      if (!agent) return

      // Mapear campos do frontend para backend
      const backendData: any = {}
      if (data.name !== undefined) backendData.name = data.name
      if (data.emoji !== undefined) backendData.avatarEmoji = data.emoji
      if (data.description !== undefined) backendData.description = data.description
      if (data.systemPrompt !== undefined) backendData.systemPrompt = data.systemPrompt
      if (data.temperature !== undefined) backendData.temperature = data.temperature
      if (data.model !== undefined) backendData.model = data.model
      if (data.color !== undefined) backendData.color = data.color
      if (data.evolutionInstanceId !== undefined) backendData.evolutionInstanceId = data.evolutionInstanceId
      if (data.toolsPrompt !== undefined) backendData.toolsPrompt = data.toolsPrompt  // ✅ Adicionar
      if (data.ragPrompt !== undefined) backendData.ragPrompt = data.ragPrompt        // ✅ Adicionar

      const response = await authenticatedFetch(`${API_ENDPOINTS.AGENTS}/${agentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(backendData)
      })
      
      if (response.ok) {
        await loadAgents()
        console.log('✅ Agente atualizado')
      }
    } catch (error) {
      console.error('❌ Erro ao atualizar agente:', error)
    }
  }

  // Função para deletar agente
  const handleDeleteAgent = async (agentId: string) => {
    try {
      const response = await authenticatedFetch(`${API_ENDPOINTS.AGENTS}/${agentId}`, {
        method: 'DELETE'
      })
      
      if (response.ok) {
        // Limpar seleção se era o agente deletado
        if (selectedAgentId === agentId) {
          setSelectedAgentId(null)
          localStorage.removeItem('selectedAgentId')
          setPromptText('')
        }
        await loadAgents()
        console.log('✅ Agente deletado')
      }
    } catch (error) {
      console.error('❌ Erro ao deletar agente:', error)
    }
  }

  // Função para atualizar nome do agente
  const handleUpdateAgent = async (agentId: string, name: string) => {
    try {
      const agent = agents.find(a => a.id === agentId)
      if (!agent) return

      const response = await authenticatedFetch(`${API_ENDPOINTS.AGENTS}/${agentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...agent, name })
      })
      
      if (response.ok) {
        await loadAgents()
        console.log('✅ Agente atualizado:', name)
      }
    } catch (error) {
      console.error('❌ Erro ao atualizar agente:', error)
    }
  }

  // Função para selecionar agente
  const handleSelectAgent = (agentId: string | null) => {
    setSelectedAgentId(agentId)
    if (agentId) {
      localStorage.setItem('selectedAgentId', agentId)
      // Carregar systemPrompt do agente
      const agent = agents.find(a => a.id === agentId)
      if (agent) {
        setPromptText(agent.systemPrompt)
        setSelectedLLMModel(agent.model)
      }
      
      // Gerar nova sessão e carregar histórico
      const newSessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
      setSessionId(newSessionId)
      localStorage.setItem('currentSessionId', newSessionId)
      console.log('[CHAT] Nova sessão criada ao trocar agente:', newSessionId)
    } else {
      localStorage.removeItem('selectedAgentId')
      setMessages([]) // Limpar mensagens quando desselecionar agente
    }
  }

  // Função para atualizar prompt no estado (sem auto-save)
  const handlePromptChange = (newPrompt: string) => {
    setPromptText(newPrompt)
  }

  // Função para carregar último prompt salvo para um RAG específico
  const loadLastPromptForRAG = async (ragId: string | null) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.PROMPTS)
      if (response.ok) {
        const data = await response.json()
        if (data.success && data.data.length > 0) {
          // Buscar o prompt mais recente para este RAG específico
          const relevantPrompts = data.data.filter((prompt: any) => 
            ragId ? prompt.rag_document_id === ragId : !prompt.rag_document_id
          )
          
          if (relevantPrompts.length > 0) {
            // Pegar o mais recente
            const latestPrompt = relevantPrompts[0] // Já vem ordenado por created_at DESC
            setPromptText(latestPrompt.content)
            console.log('📝 Prompt carregado:', latestPrompt.title)
          }
        }
      }
    } catch (error) {
      console.error('❌ Erro ao carregar prompt:', error)
    }
  }

  // Carregar prompt quando RAG for selecionado
  useEffect(() => {
    if (selectedRagId) {
      loadLastPromptForRAG(selectedRagId)
      localStorage.setItem('selectedRagId', selectedRagId)
    } else {
      localStorage.removeItem('selectedRagId')
    }
  }, [selectedRagId])

  // Carregar prompt do agente quando agentes forem carregados ou agente selecionado mudar
  useEffect(() => {
    if (selectedAgentId && agents.length > 0) {
      const agent = agents.find(a => a.id === selectedAgentId)
      if (agent && agent.systemPrompt) {
        setPromptText(agent.systemPrompt)
        console.log('✅ Prompt do agente carregado:', agent.name)
      }
    }
  }, [agents, selectedAgentId])

  // Polling de mensagens quando há agendamento ativo
  useEffect(() => {
    if (!isPollingEnabled || !selectedAgentId || !sessionId) {
      return
    }

    console.log('[POLLING] Iniciando polling - verificando novas mensagens a cada 5 segundos')
    let pollCount = 0
    const maxPolls = 60 // 5 minutos máximo (60 * 5 segundos)

    const pollInterval = setInterval(async () => {
      pollCount++
      console.log(`[POLLING] Verificação ${pollCount}/${maxPolls}`)

      try {
        const response = await authenticatedFetch(
          `${API_ENDPOINTS.AGENTS}/${selectedAgentId}/messages?sessionId=${sessionId}&limit=100`
        )

        if (response.ok) {
          const data = await response.json()

          if (data.success && data.data && data.data.length > 0) {
            const historyMessages: ChatMessage[] = data.data.map((msg: any) => ({
              id: msg.id,
              role: msg.role,
              content: msg.content,
              timestamp: msg.created_at || new Date().toISOString(),
              metadata: msg.metadata || {}
            }))

            // Verificar se há nova mensagem de confirmação de execução
            const hasExecutionConfirmation = historyMessages.some(msg =>
              msg.role === 'assistant' && msg.content.includes('Tarefa executada com sucesso')
            )

            if (hasExecutionConfirmation) {
              console.log('[POLLING] Confirmação de execução detectada - atualizando mensagens')
              setMessages(historyMessages)
              setIsPollingEnabled(false)
              console.log('[POLLING] Polling desativado - tarefa concluída')
            }
          }
        }
      } catch (error) {
        console.error('[POLLING] Erro ao verificar mensagens:', error)
      }

      // Desativar polling após timeout
      if (pollCount >= maxPolls) {
        console.log('[POLLING] Timeout atingido - desativando polling')
        setIsPollingEnabled(false)
      }
    }, 5000) // Verificar a cada 5 segundos

    return () => {
      console.log('[POLLING] Limpando intervalo de polling')
      clearInterval(pollInterval)
    }
  }, [isPollingEnabled, selectedAgentId, sessionId, authenticatedFetch])

  // Lógica de auto-sincronização removida

  // Salvamento de prompt removido nesta versão

  const handleTranscriptionComplete = async (transcription: TranscriptionResult) => {
    // Documentos vão direto para RAG, não ficam em "Transcrições Salvas"
    if (transcription.isDocument) {
      console.log('📄 Documento processado - adicionando diretamente ao RAG')
      
      // Recarregar documentos RAG para mostrar o novo documento
      await loadRAGDocuments()
      
      return // Não salvar em transcrições
    }
    
    // APENAS áudio e vídeo são salvos em "Transcrições Salvas"
    console.log('🎵 Áudio/Vídeo processado - salvando em Transcrições Salvas')
    
    // Criar estrutura da transcrição
    const savedTranscription: SavedTranscription = {
      id: generateId(),
      title: transcription.fileName 
        ? `${transcription.fileName.replace(/\.[^/.]+$/, "")}` 
        : `Transcrição ${new Date().toLocaleString('pt-BR', { 
            day: '2-digit', 
            month: '2-digit', 
            hour: '2-digit', 
            minute: '2-digit' 
          })}`,
      text: transcription.text,
      fileName: transcription.fileName,
      duration: transcription.duration,
      audioSize: transcription.audioSize,
      characterCount: transcription.text.length,
      createdAt: new Date().toISOString(),
      chunks: transcription.chunks
    }
    
    // Salvar no banco primeiro, depois adicionar ao estado local apenas se sucesso
    const success = await saveTranscriptionToDatabase(savedTranscription)
    if (success) {
      setSavedTranscriptions(prev => [savedTranscription, ...prev])
    }
  }

  // Função para salvar transcrição no banco
  const saveTranscriptionToDatabase = async (transcription: SavedTranscription): Promise<boolean> => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.TRANSCRIPTIONS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: transcription.id,
          title: transcription.title,
          content: transcription.text,
          sourceFileName: transcription.fileName,
          fileSize: transcription.audioSize,
          duration: transcription.duration,
          audioFormat: 'wav', // Pode ser obtido do arquivo original
          isAudioVideo: true, // Marcar explicitamente como áudio/vídeo
          metadata: {
            characterCount: transcription.characterCount,
            chunks: transcription.chunks
          }
        })
      })

      if (response.ok) {
        console.log('✅ Transcrição salva no banco de dados')
        return true
      } else {
        console.warn('⚠️ Erro ao salvar transcrição:', await response.text())
        return false
      }
    } catch (error) {
      console.error('❌ Erro ao salvar transcrição:', error)
      return false
    }
  }

  // Helper para mostrar mensagens de erro/sucesso
  const showMessage = (title: string, message: string) => {
    setSuccessDialog({
      isOpen: true,
      title,
      message,
      details: []
    })
  }

  // Função para otimizar prompt com IA
  const handleOptimizePrompt = async (prompt: string): Promise<void> => {
    if (!prompt.trim()) {
      showMessage('Atenção', 'Digite um prompt antes de otimizar')
      return
    }

    setIsOptimizing(true)
    console.log('🔧 Iniciando otimização do prompt...', { originalLength: prompt.length })

    try {
      const response = await authenticatedFetch(API_ENDPOINTS.OPTIMIZE_PROMPT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ prompt })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Erro na otimização')
      }

      const data = await response.json()
      
      if (data.success && data.optimizedPrompt) {
        // Atualizar o prompt com a versão otimizada
        setPromptText(data.optimizedPrompt)
        
        // Mostrar feedback de sucesso
        const improvement = data.improvement
        const changeInfo = improvement.lengthChange > 0 
          ? `+${improvement.lengthChange} caracteres` 
          : `${improvement.lengthChange} caracteres`
        
        setSuccessDialog({
          isOpen: true,
          title: 'Prompt otimizado com sucesso!',
          message: 'O prompt foi melhorado para gerar respostas mais precisas!',
          details: [
            `Original: ${improvement.originalLength} caracteres`,
            `Otimizado: ${improvement.optimizedLength} caracteres`,
            `Mudança: ${changeInfo}`
          ]
        })
        
        console.log('✅ Prompt otimizado:', { 
          originalLength: improvement.originalLength,
          optimizedLength: improvement.optimizedLength,
          improvement: improvement.lengthChange 
        })
      } else {
        throw new Error('Resposta inválida do servidor')
      }
    } catch (error) {
      console.error('❌ Erro na otimização do prompt:', error)
      showMessage('❌ Erro', `Erro ao otimizar prompt:\n${error instanceof Error ? error.message : 'Erro desconhecido'}\n\nTente novamente em alguns segundos.`)
    } finally {
      setIsOptimizing(false)
    }
  }

  // Função simulada removida — erros agora mostram mensagem real ao usuário

  // Busca do inbox WhatsApp. Extraída porque o tema neu a posiciona no grupo da
  // DIREITA (junto do Auto-líder) e o tema normal mantém à esquerda, ao lado do
  // device — mesma UI nos dois, só o ponto de montagem muda.
  const waSearchBox = (
    <div className="relative flex items-center">
      <FiSearch className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
      <input
        type="text"
        value={waSearch}
        onChange={e => setWaSearch(e.target.value)}
        placeholder="Buscar…"
        title="Buscar conversa por nome ou telefone"
        className={`h-8 sm:h-9 w-96 max-w-[45vw] rounded-lg border border-foreground/20 bg-background pl-8 pr-7 text-xs text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus-visible:ring-1 focus-visible:ring-foreground/30 transition-colors ${neuLab ? 'neu-inset' : ''}`}
      />
      {waSearch && (
        <button
          onClick={() => setWaSearch('')}
          title="Limpar busca"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <FiX className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  )

  return (
    /* .neu-stage: campo neu na RAIZ, pra que o vão ao redor da sidebar descolada
       seja o mesmo campo em que os cartões flutuam, e não o creme do dotted-bg.
       É também o PORTÃO da sidebar solta — as regras de .neu-side-float pedem
       este ancestral, então `?neu=0` devolve a barra rente sem prop nenhuma. */
    <div className={`flex h-[100dvh] dotted-bg ${neuLab ? 'neu-stage' : ''} text-foreground overflow-hidden relative`}>
      {/* ── Sidebar lateral (substitui o header horizontal) ── */}
      <CrmSidebar
        viewMode={viewMode}
        onNavigate={(mode) => {
          if (viewMode !== mode) {
            setViewMode(mode)
          }
        }}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed(c => !c)}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
        onSettingsAction={(a) => setSettingsModal(a)}
      />

      {/* ── Coluna de conteúdo ── */}
      <div className="flex-1 flex flex-col min-w-0 relative">
      {/* Banner de reconectado */}
      {showReconnectedBanner && backendHealth.status === 'online' && (
        <div className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium flex-shrink-0 z-50 bg-green-600 text-white transition-all duration-300">
          <FiCheckCircle className="w-4 h-4" />
          Servidor reconectado!
        </div>
      )}
      {/* Banner de status do backend */}
      {backendHealth.status !== 'online' && (
        <div className={`flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium flex-shrink-0 z-50 transition-all duration-300 ${
          backendHealth.status === 'offline'
            ? 'bg-red-600 text-white'
            : 'bg-yellow-500 text-black'
        }`}>
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          {backendHealth.status === 'offline'
            ? 'Servidor indisponível — reconectando automaticamente...'
            : `Reconectando ao servidor... (tentativa ${backendHealth.reconnectAttempt})`
          }
        </div>
      )}
      {/* ── Mini-header mobile (só <lg): hambúrguer + seção atual ── */}
      <header className="lg:hidden flex items-center gap-2 h-14 px-3 border-b border-border bg-card flex-shrink-0 z-20">
        <button
          onClick={() => setMobileOpen(true)}
          title="Menu"
          aria-label="Abrir menu de navegação"
          className="flex items-center justify-center w-10 h-10 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
        >
          <FiMenu className="w-5 h-5" />
        </button>
        <span className="text-base font-semibold text-foreground truncate">{crmViewLabel(viewMode)}</span>
      </header>

      {/* NEU-LAB: ancora do tema. Aqui ele e RAIZ DE TOKENS, nao de superficie —
          define as vars e permite estilizar o que nao tem className nosso
          (leaflet, recharts). Cada painel opta pela superficie com neu-canvas. */}
      <div className={`flex h-full flex-1 overflow-hidden relative z-10 ${neuLab ? 'neu-lab' : ''}`}>

        {/* Overlay para modo WhatsApp Meta - Tela cheia */}
        {viewMode === 'whatsapp' && (
          <div className={`absolute inset-0 z-50 flex flex-col dotted-bg ${neuLab ? 'neu-backdrop' : ''}`}>
            {/* Encaixotado (padrão twms 2026-07-14): respiro + cartão no desktop,
                full-bleed no mobile. bg-card SEM glass — o inbox tem overlays
                `fixed` que backdrop-filter prenderia (containing block).
                TESTE 2026-07-14: sombra no lugar da borda (mesma do mapa). */}
            <div className="flex-1 min-h-0 p-4 sm:p-[30px]">
            <div className={`h-full flex flex-col sm:rounded-xl bg-card overflow-hidden card-shadow ${neuLab ? 'neu-canvas' : ''}`}>
            {/* Barra de dispositivos */}
            <div className={`flex items-center gap-2 px-2 border-b border-border bg-card flex-shrink-0 ${neuLab ? 'neu-bar pt-2.5 pb-1.5' : 'py-1.5'}`}>

              {/* Slot da toolbar do inbox (Lista×Kanban + dropdown de campanha) —
                  no INÍCIO da barra, ANTES das abas de dispositivo. Preenchido via
                  portal pelo WhatsAppInboxPanel ativo. */}
              <div ref={setWaToolbarSlot} className="flex items-center gap-1.5 min-w-0" />

              {/* Label do device (o toggle saiu com a migração do Faturamento pro fin).
                  Padronizado com o fin em 2026-07-14: chip h-8 sm:h-9 compacto,
                  telefone no tooltip em vez de segunda linha.
                  No tema neu fica OCULTO: com um único device (o Faturamento saiu
                  pro fin) o rótulo é ruído visual e não há o que escolher. */}
              {!neuLab && (
              <div
                className="inline-flex items-center gap-1.5 h-8 sm:h-9 px-2.5 rounded-lg border border-foreground/20 bg-foreground/5 text-xs font-medium text-foreground"
                title={`Conversas WhatsApp de ${WA_DEVICE_NAME}${waDeviceInfo[WA_DEVICE_ID]?.displayPhone ? ` · ${waDeviceInfo[WA_DEVICE_ID].displayPhone}` : ''}`}
              >
                <MetaIcon className="w-4 h-4 shrink-0" />
                <span className="truncate">{WA_DEVICE_NAME}</span>
              </div>
              )}

              {/* Busca — no tema normal fica aqui, ao lado do device (padrão do
                  fin); no tema neu vai pro grupo da direita, junto do Auto-líder. */}
              {!neuLab && waSearchBox}

              {/* Extremo direito: auto-líder 24/7 + prompt do agente. */}
              <div className={`ml-auto flex items-center gap-1.5${neuLab ? ' pr-1' : ''}`}>
                {neuLab && waSearchBox}
                {/* Atribuição automática de líder 24/7 (pool Pareto) —
                    pedido do Atendimento em 15/07; mesma chave do botão
                    que também existe no toolbar do Kanban CRM. */}
                <AutoAssign247Toggle neu={neuLab} />
                {/* Prompt do agente: sem botao no front — o modal segue acessivel
                    via waAtendimentoRef.current?.openPromptModal() se voltar a ser usado. */}
              </div>
            </div>
            <div className="flex-1 overflow-hidden relative">
              <WhatsAppInboxPanel
                ref={waAtendimentoRef}
                key={`inbox-${WA_DEVICE_ID}`}
                toolbarSlot={waToolbarSlot}
                isOpen={true}
                onClose={() => {}}
                deviceId={WA_DEVICE_ID}
                deviceName={WA_DEVICE_NAME}
                // Antes vinha `true` fixo — num device sem IA (o caso do
                // Planalto) a lista desenhava selo de bot em conversa que
                // nenhum bot atende. Agora sai do próprio device.
                deviceAiEnabled={waDeviceInfo[WA_DEVICE_ID]?.aiEnabled ?? false}
                deviceApiType="meta"
                embedded={true}
                externalSearchTerm={waSearch}
                externalOpenConversationId={pendingConversationId}
                onExternalOpenHandled={() => setPendingConversationId(null)}
              />
            </div>
            </div>
            </div>
          </div>
        )}

        {/* Overlay para modo Mapa de Distribuição — encaixotado (pedido 2026-07-14):
            respiro + cartão como as demais views; bg-card sem glass (o mapa tem
            controles `fixed` do Leaflet). overflow-hidden arredonda os tiles.
            TESTE 2026-07-14: sombra no lugar da borda (comparar elegância). */}
        {viewMode === 'map' && (
          <div className={`absolute inset-0 transition-all duration-500 dotted-bg ${neuLab ? 'neu-backdrop ' : ''}${
            viewMode === 'map' ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}>
            <div className="h-full p-4 sm:p-[30px]">
            <div className={`h-full sm:rounded-xl bg-card overflow-hidden card-shadow ${neuLab ? 'neu-canvas' : ''}`}>
            <MapaDistribuicao
              markers={mapMarkers}
              onMunicipioClick={(municipio) => {
                console.log('Município clicado:', municipio)
              }}
              onAddMarker={(marker) => {
                // Verifica se já existe um marker neste município
                const exists = mapMarkers.some((m) => m.municipioId === marker.municipioId)
                if (!exists) {
                  setMapMarkers((prev) => [...prev, marker])
                  console.log('Marker adicionado:', marker)
                }
              }}
              onRemoveMarker={(markerId) => {
                setMapMarkers((prev) => prev.filter((m) => m.id !== markerId))
                console.log('Marker removido:', markerId)
              }}
              className="h-full"
              authenticatedFetch={authenticatedFetch}
            />
            </div>
            </div>
          </div>
        )}

        {/* Overlay para modo Infraestrutura DB */}
        {viewMode === 'infrastructure' && (
          <div className={`absolute inset-0 z-50 transition-all duration-500 ${
            viewMode === 'infrastructure' ? 'opacity-100' : 'opacity-0 pointer-events-none'
          } dotted-bg ${neuLab ? 'neu-backdrop' : ''}`}>
            {/* Dados e so o Dados: full-bleed ate 1280px. Abaixo disso o painel
                vira uma tela de conversa, e um cartao com 16px de respiro de
                cada lado rouba 32px de uma tela de 390 e faz a conversa parecer
                uma caixa dentro de outra. Os demais paineis seguem com o
                encaixotado padrao (p-4 sm:p-[30px]). */}
            <div className="h-full p-0 xl:p-[30px]">
            <div className={`h-full xl:rounded-xl bg-card overflow-hidden card-shadow ${neuLab ? 'neu-lab neu-canvas' : ''}`}>
            <InfrastructureDBPanel />
            </div>
            </div>
          </div>
        )}


        {/* Overlay do Agente de Campanha. Painel separado do Marketing de
            propósito: aquele lê `campaign_queue` (os disparos que já foram) e
            este escreve em `marketing_sessions`. Misturar os dois apagaria o
            catálogo de produção desta praça. */}
        {viewMode === 'marketing-agente' && (
          <div className={`absolute inset-0 z-50 transition-all duration-500 ${
            viewMode === 'marketing-agente' ? 'opacity-100' : 'opacity-0 pointer-events-none'
          } dotted-bg ${neuLab ? 'neu-backdrop' : ''}`}>
            {/* Mesma decisão do painel Dados (ver acima): a moldura de 30px
                gasta 60px de largura E de altura pra desenhar uma caixa dentro
                de outra. Numa tela de 768px de altura, com o chat espremido,
                esses 60px são duas mensagens. Só volta a partir de xl. */}
            <div className="h-full p-0 2xl:p-[30px]">
            <div className={`h-full 2xl:rounded-xl bg-card overflow-hidden card-shadow ${neuLab ? 'neu-canvas' : ''}`}>
            <AgenteCampanhaPanel />
            </div>
            </div>
          </div>
        )}

        {/* Overlay do painel Aplicativo — o app Luísa (28/09/2026). */}
        {viewMode === 'aplicativo' && (
          <div className={`absolute inset-0 z-50 transition-all duration-500 opacity-100 dotted-bg ${neuLab ? 'neu-backdrop' : ''}`}>
            <div className="h-full p-0 2xl:p-[30px]">
            <div className={`h-full 2xl:rounded-xl bg-card overflow-hidden card-shadow ${neuLab ? 'neu-canvas' : ''}`}>
            <AplicativoPanel />
            </div>
            </div>
          </div>
        )}

        {/* Overlay para modo Marketing */}
        {viewMode === 'marketing' && (
          <div className={`absolute inset-0 z-50 transition-all duration-500 ${
            viewMode === 'marketing' ? 'opacity-100' : 'opacity-0 pointer-events-none'
          } dotted-bg ${neuLab ? 'neu-backdrop' : ''}`}>
            <div className="h-full p-4 sm:p-[30px]">
            <div className={`h-full sm:rounded-xl bg-card overflow-hidden card-shadow ${neuLab ? 'neu-canvas' : ''}`}>
            <MarketingPanel />
            </div>
            </div>
          </div>
        )}

        {/* Overlay para modo Arquivos — explorer dos shares (SETORES$/PUBLICA$) via smb-gateway. Admin-only. */}
        {viewMode === 'arquivos' && (
          <div className={`absolute inset-0 z-50 transition-all duration-500 ${
            viewMode === 'arquivos' ? 'opacity-100' : 'opacity-0 pointer-events-none'
          } dotted-bg ${neuLab ? 'neu-backdrop' : ''}`}>
            <div className="h-full min-h-0 flex flex-col p-4 sm:p-5 overflow-hidden">
              {(user?.isAdmin || hasFolderAccess)
                ? <ArquivosLab authenticatedFetch={authenticatedFetch} />
                : <div className="glass-panel rounded-2xl p-6 text-sm text-muted-foreground">Você ainda não tem pastas liberadas.</div>}
            </div>
          </div>
        )}

        {/* Overlay para modo Tecnologia (Suporte) */}
        {/* Painel unificado Sistema — abas Arquitetura · Monitor · Biblioteca · Ferramentas */}
        {viewMode === 'sistema' && (
          <div className={`absolute inset-0 z-50 dotted-bg ${neuLab ? 'neu-backdrop' : ''}`}>
            <div className="h-full p-4 sm:p-[30px]">
            <div className={`h-full sm:rounded-xl bg-card overflow-hidden card-shadow ${neuLab ? 'neu-canvas' : ''}`}>
            <SistemaPanel
              initialTab={sistemaInitialTab}
              initialPath={bibliotecaInitialPath}
              onTabChange={(t) => setSistemaInitialTab(t)}
            />
            </div>
            </div>
          </div>
        )}

        {viewMode === 'bloqueados' && (
          <div className={`absolute inset-0 z-50 dotted-bg ${neuLab ? 'neu-backdrop' : ''}`}>
            <div className="h-full p-4 sm:p-[30px]">
            <div className={`h-full sm:rounded-xl bg-card overflow-hidden card-shadow ${neuLab ? 'neu-canvas' : ''}`}>
            <BloqueadosPanel />
            </div>
            </div>
          </div>
        )}

        {viewMode === 'lideres-acompanhamento' && (
          <div className={`absolute inset-0 z-50 transition-all duration-500 ${
            viewMode === 'lideres-acompanhamento' ? 'opacity-100' : 'opacity-0 pointer-events-none'
          } dotted-bg ${neuLab ? 'neu-backdrop' : ''}`}>
            <div className="h-full p-4 sm:p-[30px]">
            <div className={`h-full sm:rounded-xl bg-card overflow-hidden card-shadow ${neuLab ? 'neu-canvas' : ''}`}>
            <LeaderFollowupPanel />
            </div>
            </div>
          </div>
        )}

        {viewMode === 'lideres-cobertura' && (
          <div className={`absolute inset-0 z-50 transition-all duration-500 ${
            viewMode === 'lideres-cobertura' ? 'opacity-100' : 'opacity-0 pointer-events-none'
          } dotted-bg ${neuLab ? 'neu-backdrop' : ''}`}>
            <div className="h-full p-4 sm:p-[30px]">
            <div className={`h-full sm:rounded-xl bg-card overflow-hidden card-shadow ${neuLab ? 'neu-canvas' : ''}`}>
            <div className="h-full overflow-y-auto"><LeaderCoverageView /></div>
            </div>
            </div>
          </div>
        )}

        {viewMode === 'lideres-captacao' && (
          <div className={`absolute inset-0 z-50 transition-all duration-500 ${
            viewMode === 'lideres-captacao' ? 'opacity-100' : 'opacity-0 pointer-events-none'
          } dotted-bg ${neuLab ? 'neu-backdrop' : ''}`}>
            <div className="h-full p-4 sm:p-[30px]">
            <div className={`h-full sm:rounded-xl bg-card overflow-hidden card-shadow ${neuLab ? 'neu-canvas' : ''}`}>
            {/* A aba de edição da landing pede a sidebar recolhida: o canvas do
                editor disputa largura com ela e com as duas colunas do Puck, e
                a página acaba desenhada em 39% de zoom. O painel devolve o
                estado anterior ao sair da aba. */}
            <WizardCaptacaoPanel colapsado={collapsed} onColapsar={setCollapsed} />
            </div>
            </div>
          </div>
        )}

        {viewMode === 'lideres-pareto' && (
          <div className={`absolute inset-0 z-50 transition-all duration-500 ${
            viewMode === 'lideres-pareto' ? 'opacity-100' : 'opacity-0 pointer-events-none'
          } dotted-bg ${neuLab ? 'neu-backdrop' : ''}`}>
            <div className="h-full p-4 sm:p-[30px]">
            <div className={`h-full sm:rounded-xl bg-card overflow-hidden card-shadow ${neuLab ? 'neu-canvas' : ''}`}>
            <ParetoRankingPanel />
            </div>
            </div>
          </div>
        )}

      </div>
      </div>

      {/* Modal de Confirmação */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        details={confirmDialog.details}
        type={confirmDialog.type}
        confirmText="Excluir Permanentemente"
        cancelText="Cancelar"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      />

      {/* Modal de Sucesso */}
      <SuccessDialog
        isOpen={successDialog.isOpen}
        title={successDialog.title}
        message={successDialog.message}
        details={successDialog.details}
        onClose={() => setSuccessDialog(prev => ({ ...prev, isOpen: false }))}
      />

      {/* Modal de Editor de Prompt */}
      <PromptEditorModal
        isOpen={isPromptModalOpen}
        onClose={() => setIsPromptModalOpen(false)}
        prompt={promptText}
        onChange={handlePromptChange}
        ragDocuments={ragDocuments}
        selectedRagId={selectedRagId || undefined}
        onRagSelect={setSelectedRagId}
        onOptimizePrompt={handleOptimizePrompt}
        isOptimizing={isOptimizing}
        currentLLMModel={selectedLLMModel}
        agents={agents}
        selectedAgentId={selectedAgentId}
        onAgentSelect={handleSelectAgent}
        onAgentUpdate={handleUpdateAgent}
        onAgentUpdateFull={handleUpdateAgentFull}
        onAgentCreate={handleCreateAgent}
        onAgentDelete={handleDeleteAgent}
        authenticatedFetch={authenticatedFetch}
        enabledTools={enabledTools}
        onToolsChange={setEnabledTools}
      />

      {/* Modal de Histórico de Conversas */}
      <ConversationsPanel
        isOpen={isConversationsModalOpen}
        onClose={() => setIsConversationsModalOpen(false)}
        agentId={selectedAgentId}
      />

      {/* Modal de Atribuir Líder para o CRM */}
      {showCRMLeaderModal && (
        <AssignLeaderModal
          isOpen={showCRMLeaderModal}
          onClose={() => {
            setShowCRMLeaderModal(false)
            setCRMLeaderModalData(null)
          }}
          conversationId={crmLeaderModalData?.conversationId || 'crm-conversation'}
          deviceId="crm-device"
          contactName={crmLeaderModalData?.contactInfo?.name || 'Cliente CRM'}
          contactPhone={crmLeaderModalData?.contactInfo?.phone || ''}
          onAssign={(_leaderId, leaderData) => {
            console.log('Lead atribuído para líder no CRM:', leaderData.name)
            alert(`Lead atribuído com sucesso para: ${leaderData.name}`)
            setShowCRMLeaderModal(false)
            setCRMLeaderModalData(null)
          }}
        />
      )}

      {/* (Dropdown de Configurações migrado pro painel revelado na CrmSidebar,
          acionado pelo botão de engrenagem no rodapé → onSettingsAction.) */}

      {/* ═══ DRAWER LATERAL DE ADMINISTRAÇÃO (padrão LeaderFollowupPanel) ═══ */}
      {(settingsModal || showUserPermissions) && (
        <>
          {/* Overlay com desfoque — clique fecha */}
          <div className="fixed inset-0 z-40 glass-overlay" onClick={() => {
            setSettingsModal(null); setShowUserPermissions(null)
          }} />

          {/* Drawer */}
          <div className={`fixed top-0 right-0 bottom-0 z-50 w-full ${
            settingsModal === 'users' || (showUserPermissions && !settingsModal) ? 'sm:w-[640px]' : 'sm:w-[480px]'
          } neu-drawer flex flex-col animate-in slide-in-from-right duration-200`}>
            {/* Header do drawer */}
            <div className="flex items-center gap-2 px-4 py-3 neu-drawer-head">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg shrink-0 neu-drawer-well">
                {settingsModal === 'users' && <FiUsers className="w-5 h-5 text-violet-500" />}
                {settingsModal === 'new-user' && <LuUserPlus className="w-5 h-5 text-emerald-500" />}
                {settingsModal === 'new-device' && <FiSmartphone className="w-5 h-5 text-blue-500" />}
                {showUserPermissions && !settingsModal && <FiSettings className="w-5 h-5 text-amber-500" />}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
                {settingsModal === 'users' ? 'Usuários e Permissões' :
                 settingsModal === 'new-user' ? 'Novo Usuário' :
                 settingsModal === 'new-device' ? 'Novo Dispositivo Meta' :
                 showUserPermissions ? `Permissões — ${showUserPermissions.user?.fullName || showUserPermissions.user?.username}` :
                 ''}
              </span>
              <button onClick={() => { setSettingsModal(null); setShowUserPermissions(null) }}
                title="Fechar"
                className="p-1.5 rounded-lg neu-drawer-x hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors">
                <FiX className="w-4 h-4" />
              </button>
            </div>

            {/* Corpo do drawer */}
            <div className="flex-1 overflow-y-auto">
              {settingsModal === 'users' && (
                <SettingsPanel
                  key={settingsPanelKey}
                  onOpenPermissions={(u, permissions, devices) => {
                    setSettingsModal(null)
                    setShowUserPermissions({ user: u, permissions, devices })
                  }}
                />
              )}
              {settingsModal === 'new-user' && (
                <CreateUserPanel
                  onClose={() => setSettingsModal(null)}
                  onUserCreated={() => {
                    setSettingsPanelKey(prev => prev + 1)
                    setSettingsModal(null)
                  }}
                  authenticatedFetch={authenticatedFetch}
                />
              )}
              {settingsModal === 'new-device' && (
                <NewDevicePanel
                  mode="meta"
                  onClose={() => setSettingsModal(null)}
                  onDeviceCreated={() => {
                    setSettingsPanelKey(prev => prev + 1)
                    setSettingsModal(null)
                  }}
                />
              )}
              {showUserPermissions && !settingsModal && (
                <UserPermissionsPanel
                  user={showUserPermissions.user}
                  permissions={showUserPermissions.permissions}
                  devices={showUserPermissions.devices}
                  onClose={() => setShowUserPermissions(null)}
                  onUserUpdated={() => setSettingsPanelKey(k => k + 1)}
                  authenticatedFetch={authenticatedFetch}
                />
              )}
            </div>
          </div>
        </>
      )}

    </div>
  )
}

// Importar mapas públicos
import MapaExternoAuth from './MapaExternoAuth'
import MapaExterno from './MapaExterno'
import SejaConsultora from './SejaConsultora'

// Verificar se está na rota do mapa (com autenticação por CPF)
const IS_MAPA = window.location.pathname === '/mapa' || window.location.pathname === '/mapa/'
const IS_WIZARD = window.location.pathname === '/seja-consultora' || window.location.pathname === '/seja-consultora/'
// A landing /consultora NÃO passa por aqui: ela tem entry próprio
// (consultora.html + src/landing/main.tsx) para não carregar o bundle do CRM.

if (IS_MAPA) {
  console.log('[APP] 🗺️ Rota do mapa ativada (com autenticação)')
}

// Wrapper que decide entre mostrar login ou aplicação principal
function AuthWrapper() {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="h-screen bg-background text-foreground flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg">Verificando autenticação...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return <AuthLogin />
  }

  // Troca pendente = o app NÃO monta. Não é modal por cima: com a pendência o
  // servidor responde 423 em toda escrita, então mostrar o sistema atrás seria
  // oferecer uma tela que não funciona.
  if (user.mustChangePassword) {
    return <TrocaSenhaObrigatoria />
  }

  // Escuta ativa global da Clara (botão no rodapé da sidebar) — vive acima do
  // MainApp pra sobreviver à navegação entre painéis; logout desmonta e para.
  return (
    <ClaraVoiceProvider>
      <MainApp />
    </ClaraVoiceProvider>
  )
}

// Componente raiz que decide se mostra mapa, wizard ou app normal
function App() {
  // WIZARD: landing pública "Quero ser Consultora"
  if (IS_WIZARD) {
    return <SejaConsultora />
  }

  // MAPA: Se está em /mapa, verifica autenticação e mostra login ou mapa
  if (IS_MAPA) {
    const token = sessionStorage.getItem('mapaToken')

    // Se tem token, mostra o mapa autenticado
    if (token) {
      return <MapaExterno />
    }

    // Se não tem token, mostra a página de login
    return <MapaExternoAuth />
  }

  // App normal com autenticação
  return (
    <ThemeProvider>
      <AuthProvider>
        <AuthWrapper />
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App